import { useAppSelector } from '@/store/hooks';
import { getWorkingProjectUuid, selectWorkingProject, selectWorkingSession } from '@/store/slices/project/selectors';
import useLanguageParams from '@/utils/use-language-params';
import { useCallback, useEffect, useRef, type DetailedHTMLProps, type IframeHTMLAttributes } from 'react';
import { isObject } from 'underscore';
import styles from './video-embed.module.css';
import { getBaseAdminURL } from '@/utils/urls';

export default function VideoIframe(
  props: DetailedHTMLProps<IframeHTMLAttributes<HTMLIFrameElement> & { fallbackVideo?: string }, HTMLIFrameElement>
) {
  const {
    height = 1080 / 2,
    width = 1920 / 2,
    allow = 'encrypted-media *; fullscreen',
    fallbackVideo,
    ...rest
  } = props;
  const workingProject = useAppSelector(selectWorkingProject);
  const session = useAppSelector(selectWorkingSession);
  const sessionUuid = session?.uuid ?? workingProject?.sessions?.[0]?.uuid;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const language = useLanguageParams();
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const baseAdminUrl = getBaseAdminURL();

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      // we need to await the iframe's passage through the auth gate
      // before sending it the working project payload. events-3 will send a message
      // up to this app notifying it that the player is ready to accept the event payload
      if (isObject(e.data) && e.data.ready) {
        iframeRef.current?.contentWindow?.postMessage({ suppressWatermark: true }, baseAdminUrl);
        if (baseAdminUrl && e.origin === baseAdminUrl) {
          iframeRef.current?.contentWindow?.postMessage({ workingEvent: workingProject, fallbackVideo }, baseAdminUrl);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [workingProject, baseAdminUrl, fallbackVideo]);

  const handleError = useCallback((e: unknown) => {
    console.error(e);
  }, []);

  return (
    <iframe
      {...rest}
      className={styles.videoEmbed}
      ref={iframeRef}
      height={height}
      width={width}
      allow={allow}
      onError={handleError}
      src={`${baseAdminUrl}/media/${projectUuid}/${sessionUuid}/${language}?embed=true`}
    />
  );
}
